import { useEffect, useRef, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Dna, Palette, Upload, Trash2, Check, AlertTriangle, RotateCw, Move, ImagePlus, Eye, Flame } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND}${p}`) : null);

const ModelGenes = () => {
  const [data, setData] = useState(null); // {loci, labels, default, models}
  const [selId, setSelId] = useState("");
  const [gt, setGt] = useState({});
  const [pheno, setPheno] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = () => gameApi.get("/game/admin/hma/model-genes").then((r) => setData(r.data)).catch(() => setData({ models: [] }));
  useEffect(() => { load(); }, []);

  const models = data?.models || [];
  const filtered = models.filter((m) => (m.display_name || m.model_id).toLowerCase().includes(q.toLowerCase()));
  const cur = models.find((m) => m.model_id === selId);

  const openModel = (m) => { setSelId(m.model_id); setGt({ ...m.genotype }); setPheno(m.phenotype); };
  const setAllele = (locus, idx, val) => {
    const parts = (gt[locus] || data.default[locus]).split("/");
    parts[idx] = val;
    setGt({ ...gt, [locus]: parts.join("/") });
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data: res } = await gameApi.put("/game/admin/hma/model-genes", { model_id: selId, genotype: gt });
      setPheno(res.phenotype);
      toast.success(`Saved — ${res.phenotype.base_color}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (!data) return <Loading />;
  return (
    <div className="grid gap-3 md:grid-cols-[minmax(0,240px)_1fr]" data-testid="hma-genes-panel">
      <div className="space-y-2">
        <input data-testid="hma-genes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search coat models…" className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-xs" />
        <div className="max-h-[420px] space-y-1 overflow-auto rounded-lg border border-border p-1">
          {filtered.map((m) => (
            <button key={m.model_id} data-testid={`hma-genes-model-${m.model_id}`} onClick={() => openModel(m)}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${selId === m.model_id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              <span className="min-w-0 flex-1 truncate font-bold">{m.display_name}</span>
              {m.configured ? <Check size={13} className="shrink-0" /> : <span className="shrink-0 text-[10px] opacity-70">default</span>}
            </button>
          ))}
          {filtered.length === 0 && <p className="p-2 text-xs text-muted-foreground">No models.</p>}
        </div>
      </div>
      <div>
        {!cur ? <p className="p-4 text-sm text-muted-foreground">Select a coat model to assign its canonical genotype.</p> : (
          <div className="space-y-3 rounded-xl border border-border p-3" data-testid="hma-genes-editor">
            <div className="flex items-center gap-2">
              <Dna size={18} className="text-primary" />
              <div><p className="font-display text-base font-bold text-primary">{cur.display_name}</p>
                <p className="text-[11px] text-muted-foreground">body: {cur.body_model} · {cur.hma_collection || "base"}</p></div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {Object.keys(data.loci).map((locus) => {
                const parts = (gt[locus] || data.default[locus]).split("/");
                return (
                  <div key={locus} className="rounded-lg bg-muted/40 p-2">
                    <p className="mb-1 text-[11px] font-bold">{data.labels[locus] || locus}</p>
                    <div className="flex items-center gap-1">
                      {[0, 1].map((i) => (
                        <select key={i} data-testid={`hma-gene-${locus}-${i}`} value={parts[i]} onChange={(e) => setAllele(locus, i, e.target.value)}
                          className="flex-1 rounded border border-border bg-background px-1 py-1 text-xs">
                          {data.loci[locus].map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {pheno && (
              <div className="rounded-lg border border-border bg-card p-2 text-xs" data-testid="hma-genes-phenotype">
                <span className="font-bold text-primary">{pheno.base_color}</span>
                <span className="text-muted-foreground"> · {pheno.base_family}
                  {pheno.gray ? " · Gray" : ""}{pheno.tobiano ? " · Tobiano" : ""}{pheno.roan ? " · Roan" : ""}
                  {pheno.overo ? " · Overo" : ""}{pheno.lp ? " · Appaloosa" : ""}</span>
                {pheno.display_note && <span className="text-secondary"> · {pheno.display_note}</span>}
              </div>
            )}
            <button data-testid="hma-genes-save" disabled={busy} onClick={save}
              className="w-full rounded-lg bg-primary py-2 text-sm font-bold text-primary-foreground disabled:opacity-50">
              {busy ? "Saving…" : "Save Genotype"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MarkingEditor = ({ asset, bases, onClose, onSaved }) => {
  const norm = (L, i) => ({ id: L.id, name: L.name || `Layer ${i + 1}`, image_path: L.image_path,
    x: L.x || 0, y: L.y || 0, scale: L.scale ?? 100, rot: L.rot || 0 });
  const [layers, setLayers] = useState((asset.layers || []).map(norm));
  const [selId, setSelId] = useState((asset.layers || [])[0]?.id || null);
  const [busy, setBusy] = useState(false);
  const bodyBases = bases.filter((b) => (asset.body_model === "*" ? true : b.body_model === asset.body_model));
  const [backdrop, setBackdrop] = useState(bodyBases[0]?.image_path || bases[0]?.image_path || null);
  const canvasRef = useRef();
  const fileRef = useRef();
  const drag = useRef(null);

  const sel = layers.find((L) => L.id === selId) || null;
  const setSel = (patch) => setLayers((ls) => ls.map((L) => (L.id === selId ? { ...L, ...patch } : L)));
  const base = { slot: asset.slot, key: asset.key, body_model: asset.body_model };

  const onDown = (e) => { if (!sel) return; drag.current = { sx: e.clientX, sy: e.clientY, ox: sel.x, oy: sel.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const onMove = (e) => {
    if (!drag.current || !canvasRef.current) return;
    const r = canvasRef.current.getBoundingClientRect();
    const dx = ((e.clientX - drag.current.sx) / r.width) * 100;
    const dy = ((e.clientY - drag.current.sy) / r.height) * 100;
    setSel({ x: Math.round((drag.current.ox + dx) * 10) / 10, y: Math.round((drag.current.oy + dy) * 10) / 10 });
  };
  const onUp = () => { drag.current = null; };

  const uploadLayer = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("slot", asset.slot); fd.append("key", asset.key); fd.append("body_model", asset.body_model);
      const { data } = await gameApi.post("/game/admin/hma/assets/layers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Merge in ONLY the new layer so existing layers keep their in-progress (unsaved) placement.
      const added = norm(data.layer, layers.length);
      const next = [...layers, added];
      setLayers(next); setSelId(added.id); onSaved({ ...asset, layers: next });
      toast.success("Layer added");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const delLayer = async (id) => {
    try {
      await gameApi.delete(`/game/admin/hma/assets/layers?slot=${encodeURIComponent(asset.slot)}&key=${encodeURIComponent(asset.key)}&body_model=${encodeURIComponent(asset.body_model)}&layer_id=${id}`);
      // Only drop the removed layer locally; keep the others' in-progress placement untouched.
      const next = layers.filter((L) => L.id !== id);
      setLayers(next); if (selId === id) setSelId(next[0]?.id || null); onSaved({ ...asset, layers: next });
      toast.success("Layer removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    if (!layers.length) { toast.error("Add at least one layer"); return; }
    setBusy(true);
    try {
      for (const L of layers)
        await gameApi.put("/game/admin/hma/assets/layers/transform", { ...base, layer_id: L.id, x: L.x, y: L.y, scale: L.scale, rot: L.rot, name: L.name });
      toast.success("Placement saved"); onSaved({ ...asset, layers }); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="marking-editor" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-sm space-y-3 overflow-y-auto rounded-2xl bg-card p-4" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-bold text-primary">Position {asset.slot}:{asset.key} · {asset.body_model === "*" ? "all bodies" : asset.body_model}</p>
        {bodyBases.length > 1 && (
          <select value={backdrop || ""} onChange={(e) => setBackdrop(e.target.value)} data-testid="marking-editor-backdrop" className="w-full rounded-lg border border-border bg-background px-2 py-1 text-xs">
            {bodyBases.map((b) => <option key={b.image_path} value={b.image_path}>{b.art_phenotype} v{b.variant}</option>)}
          </select>
        )}
        <div ref={canvasRef} className="relative mx-auto aspect-square w-full max-w-[300px] touch-none overflow-hidden rounded-xl border border-border bg-gradient-to-b from-amber-50 to-stone-100">
          {backdrop ? <img src={imgUrl(backdrop)} alt="base" className="absolute inset-0 h-full w-full object-contain opacity-90" draggable={false} /> : <div className="grid h-full place-items-center px-3 text-center text-[11px] text-muted-foreground">Upload a base coat for this body to preview against.</div>}
          {layers.map((L) => (
            <img key={L.id} src={imgUrl(L.image_path)} alt={L.name} onPointerDown={L.id === selId ? onDown : undefined} onPointerMove={L.id === selId ? onMove : undefined} onPointerUp={L.id === selId ? onUp : undefined} draggable={false}
              className={`absolute inset-0 h-full w-full select-none object-contain ${L.id === selId ? "cursor-grab active:cursor-grabbing ring-2 ring-primary" : "pointer-events-none opacity-90"}`}
              data-testid={L.id === selId ? "marking-editor-overlay" : `marking-layer-img-${L.id}`}
              style={{ transform: `translate(${L.x}%, ${L.y}%) scale(${L.scale / 100}) rotate(${L.rot}deg)`, transformOrigin: "center" }} />
          ))}
        </div>
        {/* Layers list */}
        <div className="space-y-1" data-testid="marking-layers">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold text-muted-foreground">Layers ({layers.length})</p>
            <label data-testid="marking-add-layer" className={`flex cursor-pointer items-center gap-1 rounded-full border border-primary px-2 py-0.5 text-[10px] font-bold text-primary ${busy ? "opacity-50" : ""}`}>
              {busy ? <Loader2 className="animate-spin" size={11} /> : <ImagePlus size={11} />} Add Layer
              <input ref={fileRef} type="file" accept="image/png,image/webp" className="hidden" disabled={busy} onChange={(e) => uploadLayer(e.target.files?.[0])} />
            </label>
          </div>
          {layers.length === 0 && <p className="text-[10px] text-muted-foreground">No layers yet. Upload one or more transparent PNGs for this marking.</p>}
          {layers.map((L) => (
            <div key={L.id} data-testid={`marking-layer-${L.id}`} onClick={() => setSelId(L.id)}
              className={`flex items-center gap-2 rounded-lg border p-1 text-[11px] ${L.id === selId ? "border-primary bg-primary/10" : "border-border"}`}>
              <img src={imgUrl(L.image_path)} alt={L.name} className="h-8 w-8 shrink-0 rounded bg-muted/40 object-contain" />
              <input value={L.name} onClick={(e) => e.stopPropagation()} onChange={(e) => setLayers((ls) => ls.map((x) => (x.id === L.id ? { ...x, name: e.target.value } : x)))}
                className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 font-bold focus:border-border" data-testid={`marking-layer-name-${L.id}`} />
              <button onClick={(e) => { e.stopPropagation(); delLayer(L.id); }} data-testid={`marking-layer-del-${L.id}`} className="shrink-0 rounded bg-rose-100 p-1 text-rose-600"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
        {/* Placement controls for the SELECTED layer */}
        {sel ? (
          <>
            <p className="text-[10px] text-muted-foreground">Editing <span className="font-bold">{sel.name}</span> — drag it in the preview or use the sliders. Each layer saves its own placement per body model.</p>
            {[["scale", "Scale %", 10, 200, 1], ["rot", "Rotation°", -180, 180, 1], ["x", "X offset %", -60, 60, 0.5], ["y", "Y offset %", -60, 60, 0.5]].map(([k, lbl, mn, mx, st]) => (
              <div key={k} className="flex items-center gap-2">
                <label className="w-24 shrink-0 text-[11px] font-bold">{lbl}</label>
                <input type="range" min={mn} max={mx} step={st} value={sel[k]} onChange={(e) => setSel({ [k]: Number(e.target.value) })} className="flex-1" data-testid={`marking-editor-${k}`} />
                <input type="number" value={sel[k]} onChange={(e) => setSel({ [k]: Number(e.target.value) })} className="w-16 rounded border border-border bg-background px-1 py-0.5 text-[11px]" />
              </div>
            ))}
            <button onClick={() => setSel({ x: 0, y: 0, scale: 100, rot: 0 })} data-testid="marking-editor-reset" className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold">Reset layer</button>
          </>
        ) : <p className="text-[10px] text-muted-foreground">Select a layer to edit its placement.</p>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 rounded-lg border border-border px-3 py-1.5 text-xs font-bold">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="marking-editor-save" className="flex-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-50">{busy ? "Saving…" : "Save Placement"}</button>
        </div>
      </div>
    </div>
  );
};

const CoatAssets = () => {
  const [cat, setCat] = useState(null); // {slots:{slot:{label,categories:[{cat,label}]}}, body_models}
  const [assets, setAssets] = useState(null);
  const [slot, setSlot] = useState("");
  const [mcat, setMcat] = useState(""); // marking category within the slot
  const [variant, setVariant] = useState(1);
  const [body, setBody] = useState("*");
  const [busy, setBusy] = useState(false);
  const [bases, setBases] = useState([]);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef();

  const loadAssets = () => gameApi.get("/game/admin/hma/assets").then((r) => setAssets(r.data.assets)).catch(() => setAssets([]));
  useEffect(() => {
    gameApi.get("/game/admin/hma/asset-catalog").then((r) => {
      setCat(r.data);
      const s = Object.keys(r.data.slots)[0];
      setSlot(s); setMcat(r.data.slots[s].categories[0].cat);
    });
    gameApi.get("/game/admin/hma/base-art").then((r) => setBases(r.data.base_art)).catch(() => setBases([]));
    loadAssets();
  }, []);

  const key = `${mcat}${variant}`;
  const pickSlot = (s) => { setSlot(s); setMcat(cat.slots[s].categories[0].cat); setVariant(1); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("slot", slot); fd.append("key", key); fd.append("body_model", body);
      await gameApi.post("/game/admin/hma/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Mapped ${slot}:${key}`); loadAssets();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const del = async (a) => {
    try { await gameApi.delete(`/game/admin/hma/assets?slot=${encodeURIComponent(a.slot)}&key=${encodeURIComponent(a.key)}&body_model=${encodeURIComponent(a.body_model)}`); toast.success("Removed"); loadAssets(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!cat || !assets) return <Loading />;
  const bodyOpts = ["*", ...cat.body_models];
  const cats = cat.slots[slot]?.categories || [];
  return (
    <div className="space-y-4" data-testid="hma-assets-panel">
      <div className="space-y-2 rounded-xl border border-border p-3">
        <p className="flex items-center gap-1 text-sm font-bold text-primary"><Palette size={15} /> White Marking / Pattern Overlays</p>
        <p className="text-[11px] text-muted-foreground">These layer on TOP of the painted base image. Render order: Appaloosa (bottom) → Tobiano → Overo → legs → face. Upload as many <span className="font-bold">variants</span> as you like per category — horses pick one evenly (1/N). They never carry body coat color.</p>
        <div className="grid gap-2 sm:grid-cols-4">
          <div><p className="mb-1 text-[11px] font-bold">Layer slot</p>
            <select data-testid="hma-asset-slot" value={slot} onChange={(e) => pickSlot(e.target.value)} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
              {Object.entries(cat.slots).map(([s, v]) => <option key={s} value={s}>{v.label || s}</option>)}
            </select></div>
          <div><p className="mb-1 text-[11px] font-bold">Marking</p>
            <select data-testid="hma-asset-cat" value={mcat} onChange={(e) => { setMcat(e.target.value); setVariant(1); }} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
              {cats.map((c) => <option key={c.cat} value={c.cat}>{c.label}</option>)}
            </select></div>
          <div><p className="mb-1 text-[11px] font-bold">Variant #</p>
            <input data-testid="hma-asset-variant" type="number" min={1} value={variant} onChange={(e) => setVariant(Math.max(1, Number(e.target.value) || 1))} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs" /></div>
          <div><p className="mb-1 text-[11px] font-bold">Body model</p>
            <select data-testid="hma-asset-body" value={body} onChange={(e) => setBody(e.target.value)} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
              {bodyOpts.map((b) => <option key={b} value={b}>{b === "*" ? "All bodies (*)" : b}</option>)}
            </select></div>
        </div>
        <label data-testid="hma-asset-upload" className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-primary py-2 text-xs font-bold text-primary ${busy ? "opacity-50" : ""}`}>
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload PNG/WebP for {slot}:{key}
          <input ref={fileRef} type="file" accept="image/png,image/webp" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      <div>
        <p className="mb-2 text-xs font-bold text-muted-foreground">Mapped assets ({assets.length})</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {assets.map((a, i) => (
            <div key={i} data-testid={`hma-asset-${a.slot}-${a.key}`} className="rounded-lg border border-border bg-card p-1.5">
              <div className="relative grid aspect-square place-items-center overflow-hidden rounded bg-muted/40">
                {(a.layers?.length ? a.layers : (a.image_path ? [{ id: "base", image_path: a.image_path, x: a.x, y: a.y, scale: a.scale, rot: a.rot }] : [])).length === 0
                  ? <span className="text-[10px] text-muted-foreground">no image</span>
                  : (a.layers?.length ? a.layers : [{ id: "base", image_path: a.image_path, x: a.x, y: a.y, scale: a.scale, rot: a.rot }]).map((L, li) => (
                      <img key={L.id || li} src={imgUrl(L.image_path)} alt={a.key} className="absolute inset-0 h-full w-full object-contain"
                        style={{ zIndex: li + 1, transform: `translate(${L.x || 0}%, ${L.y || 0}%) scale(${(L.scale ?? 100) / 100}) rotate(${L.rot || 0}deg)`, transformOrigin: "center" }} />
                    ))}
              </div>
              <p className="mt-1 truncate text-[11px] font-bold">{a.slot}:{a.key}</p>
              <p className="text-[10px] text-muted-foreground">{a.body_model === "*" ? "all bodies" : a.body_model}</p>
              <p className="text-[9px] text-muted-foreground">{(a.layers?.length || 0)} layer{(a.layers?.length || 0) === 1 ? "" : "s"}</p>
              <div className="mt-1 flex gap-1">
                {(a.image_path || a.layers?.length) ? <button data-testid={`hma-asset-pos-${a.slot}-${a.key}`} onClick={() => setEditing(a)} className="flex flex-1 items-center justify-center gap-1 rounded bg-sky-100 py-1 text-[10px] font-bold text-sky-700"><Move size={11} /> Layers</button> : null}
                <button data-testid={`hma-asset-del-${a.slot}-${a.key}`} onClick={() => del(a)} className="flex flex-1 items-center justify-center gap-1 rounded bg-rose-100 py-1 text-[10px] font-bold text-rose-600"><Trash2 size={11} /> Remove</button>
              </div>
            </div>
          ))}
          {assets.length === 0 && <p className="col-span-full text-xs text-muted-foreground">No coat art mapped yet. Genetics still work — horses use a safe fallback until art is uploaded.</p>}
        </div>
      </div>
      {editing && <MarkingEditor asset={editing} bases={bases} onClose={() => setEditing(null)} onSaved={(u) => setAssets((prev) => prev.map((x) => (x.slot === u.slot && x.key === u.key && x.body_model === u.body_model ? u : x)))} />}
      <ManeMasks />
    </div>
  );
};

const ManeMasks = () => {
  const [data, setData] = useState(null); // { body_models, masks }
  const [busy, setBusy] = useState("");
  const [warm, setWarm] = useState({}); // body_model -> { state: "running"|"done"|"failed", msg }
  const [warmAll, setWarmAll] = useState(null); // null | { state, msg }
  const load = () => gameApi.get("/game/admin/hma/mane-masks").then((r) => setData(r.data)).catch(() => setData({ body_models: [], masks: {} }));
  useEffect(() => { load(); }, []);
  const upload = async (body, file) => {
    if (!file) return;
    setBusy(body);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("body_model", body);
      const { data: r } = await gameApi.post("/game/admin/hma/mane-mask", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Mane mask saved · cache cleared (${r.cache_cleared})`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy("");
  };
  const remove = async (body) => {
    if (!window.confirm(`Remove the mane mask for ${body}? This body will render exactly as it does now (no mane restore).`)) return;
    setBusy(body);
    try { const { data: r } = await gameApi.delete(`/game/admin/hma/mane-mask?body_model=${encodeURIComponent(body)}`); toast.success(`Removed · cache cleared (${r.cache_cleared})`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    setBusy("");
  };
  const prewarm = async (body) => {
    setWarm((w) => ({ ...w, [body]: { state: "running" } }));
    try {
      const { data: r } = await gameApi.post(`/game/admin/hma/marking-bake/prewarm?body_model=${encodeURIComponent(body)}`);
      setWarm((w) => ({ ...w, [body]: { state: "done", msg: `${r.baked} baked / ${r.unique_recipes} combos` } }));
    } catch (e) {
      setWarm((w) => ({ ...w, [body]: { state: "failed", msg: e.response?.data?.detail || "" } }));
    }
  };
  const prewarmAll = async () => {
    setWarmAll({ state: "running" });
    try {
      const { data: r } = await gameApi.post(`/game/admin/hma/marking-bake/prewarm`);
      setWarmAll({ state: "done", msg: `${r.baked} baked / ${r.unique_recipes} combos` });
    } catch (e) {
      setWarmAll({ state: "failed", msg: e.response?.data?.detail || "" });
    }
  };
  if (!data) return null;
  const anyWarming = warmAll?.state === "running" || Object.values(warm).some((w) => w?.state === "running");
  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-3" data-testid="mane-masks">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1 text-sm font-bold text-primary"><ImagePlus size={15} /> Mane / Forelock Masks</p>
        <div className="flex items-center gap-2">
          {warmAll && (
            <span data-testid="prewarm-all-status" className={`text-[10px] font-bold ${warmAll.state === "done" ? "text-emerald-600" : warmAll.state === "failed" ? "text-rose-600" : "text-muted-foreground"}`}>
              {warmAll.state === "running" ? "Prewarming…" : warmAll.state === "done" ? `Complete · ${warmAll.msg}` : `Failed${warmAll.msg ? ` · ${warmAll.msg}` : ""}`}
            </span>
          )}
          <button data-testid="prewarm-all-btn" onClick={prewarmAll} disabled={anyWarming} className="flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[10px] font-bold text-primary-foreground disabled:opacity-50">
            {warmAll?.state === "running" ? <Loader2 className="animate-spin" size={11} /> : <Flame size={11} />} Prewarm all body models
          </button>
        </div>
      </div>
      <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">Mane masks restore the original mane/forelock above white markings. Use one transparent mask per body model — it's shared by every coat colour on that body and is aligned 1:1 (no scale/rotation/offset). Uploading clears that body's cache; run Prewarm when you're done making changes.</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {data.body_models.map((body) => {
          const mask = data.masks[body];
          const w = warm[body];
          return (
            <div key={body} data-testid={`mane-mask-${body}`} className="flex items-center gap-3 rounded-lg border border-border bg-background p-2">
              <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-md border border-border bg-[repeating-conic-gradient(#e5e5e5_0_25%,#fff_0_50%)] bg-[length:12px_12px]">
                {mask ? <img src={imgUrl(mask)} alt="mane mask" className="h-full w-full object-contain" data-testid={`mane-mask-thumb-${body}`} /> : <span className="text-[9px] text-muted-foreground">none</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-primary">{body}</p>
                <p className={`text-[10px] font-bold ${mask ? "text-emerald-600" : "text-muted-foreground"}`}>{mask ? "Mask set" : "No mask"}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <label data-testid={`mane-mask-upload-${body}`} className={`flex cursor-pointer items-center gap-1 rounded-full border border-primary px-2 py-0.5 text-[10px] font-bold text-primary ${busy === body ? "opacity-50" : ""}`}>
                    {busy === body ? <Loader2 className="animate-spin" size={10} /> : <Upload size={10} />} {mask ? "Replace" : "Upload"}
                    <input type="file" accept="image/*" className="hidden" disabled={busy === body} onChange={(e) => upload(body, e.target.files?.[0])} />
                  </label>
                  {mask && <button data-testid={`mane-mask-del-${body}`} onClick={() => remove(body)} disabled={busy === body} className="flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-600 disabled:opacity-50"><Trash2 size={10} /> Remove</button>}
                  <button data-testid={`mane-mask-prewarm-${body}`} onClick={() => prewarm(body)} disabled={anyWarming} className="flex items-center gap-1 rounded-full border border-amber-500 px-2 py-0.5 text-[10px] font-bold text-amber-600 disabled:opacity-50">
                    {w?.state === "running" ? <Loader2 className="animate-spin" size={10} /> : <Flame size={10} />} Prewarm this body
                  </button>
                </div>
                {w && (
                  <p data-testid={`mane-mask-prewarm-status-${body}`} className={`mt-1 text-[10px] font-bold ${w.state === "done" ? "text-emerald-600" : w.state === "failed" ? "text-rose-600" : "text-muted-foreground"}`}>
                    {w.state === "running" ? "Prewarming…" : w.state === "done" ? `Complete · ${w.msg}` : `Failed${w.msg ? ` · ${w.msg}` : ""}`}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BaseCoatArt = () => {
  const [cat, setCat] = useState(null); // {art_phenotypes, body_models, variants, completion}
  const [rows, setRows] = useState(null);
  const [body, setBody] = useState("");
  const [ap, setAp] = useState("");
  const [variant, setVariant] = useState(1);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const loadCat = () => gameApi.get("/game/admin/hma/base-art-catalog").then((r) => {
    setCat(r.data);
    setBody((b) => b || r.data.body_models[0] || "");
    setAp((a) => a || r.data.art_phenotypes[0]?.slug || "");
  });
  const loadRows = () => gameApi.get("/game/admin/hma/base-art").then((r) => setRows(r.data.base_art)).catch(() => setRows([]));
  useEffect(() => { loadCat(); loadRows(); }, []);

  const upload = async (file) => {
    if (!file || !body) { toast.error("Pick a body model first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("body_model", body); fd.append("art_phenotype", ap); fd.append("variant", variant);
      await gameApi.post("/game/admin/hma/base-art/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Base art saved — ${body} · ${ap} v${variant}`); loadRows(); loadCat();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const del = async (a) => {
    try { await gameApi.delete(`/game/admin/hma/base-art?body_model=${encodeURIComponent(a.body_model)}&art_phenotype=${encodeURIComponent(a.art_phenotype)}&variant=${a.variant}`); toast.success("Removed"); loadRows(); loadCat(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!cat || !rows) return <Loading />;
  const isUniversal = body.startsWith("universal_");
  const doneFor = (slug) => (cat.completion?.[body]?.[slug] || 0) > 0; // ✅ once ≥1 variant exists
  const doneCount = cat.art_phenotypes.filter((p) => doneFor(p.slug)).length;
  return (
    <div className="space-y-4" data-testid="hma-base-art-panel">
      <div className="space-y-2 rounded-xl border border-border p-3">
        <p className="flex items-center gap-1 text-sm font-bold text-primary"><Palette size={15} /> Upload Painted Base Coat</p>
        <p className="text-[11px] text-muted-foreground">One painted base per body model + art phenotype (+ optional variant). Roan / Dun / Cream / Gray are painted in — never layered. The phenotype list grows automatically as new colors are bred. <span className="font-bold">✅ = art uploaded, ❗ = still needed</span> for the selected body.</p>
        <div className="grid gap-2 sm:grid-cols-3">
          <div><p className="mb-1 text-[11px] font-bold">Body model</p>
            <select data-testid="base-art-body" value={body} onChange={(e) => { const b = e.target.value; setBody(b); if (b.startsWith("universal_")) setVariant(1); }} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
              {cat.body_models.map((b) => <option key={b} value={b}>{b}</option>)}
            </select></div>
          <div><p className="mb-1 text-[11px] font-bold">Art phenotype <span className="text-emerald-600">({doneCount}/{cat.art_phenotypes.length} done)</span></p>
            <select data-testid="base-art-pheno" value={ap} onChange={(e) => setAp(e.target.value)} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
              {cat.art_phenotypes.map((p) => <option key={p.slug} value={p.slug}>{doneFor(p.slug) ? "✅" : "❗"} {p.label} ({p.slug})</option>)}
            </select></div>
          <div><p className="mb-1 text-[11px] font-bold">Variant #</p>
            <input data-testid="base-art-variant" type="number" min={1} value={variant} onChange={(e) => setVariant(Math.max(1, Number(e.target.value) || 1))} disabled={isUniversal} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs disabled:opacity-60" />
            {isUniversal && <p className="mt-0.5 text-[9px] text-muted-foreground">Foal/juvenile use a single variant per phenotype.</p>}</div>
        </div>
        <label data-testid="base-art-upload" className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-primary py-2 text-xs font-bold text-primary ${busy ? "opacity-50" : ""}`}>
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload PNG/WebP for {body}:{ap} v{variant}
          <input ref={fileRef} type="file" accept="image/png,image/webp" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      <div data-testid="base-art-status">
        <p className="mb-2 text-xs font-bold text-muted-foreground">Phenotype coverage for <span className="text-primary">{body}</span></p>
        <div className="flex flex-wrap gap-1.5">
          {cat.art_phenotypes.map((p) => (
            <button key={p.slug} data-testid={`base-art-chip-${p.slug}`} onClick={() => setAp(p.slug)}
              className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${doneFor(p.slug) ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
              {doneFor(p.slug) ? "✅" : "❗"} {p.label}{doneFor(p.slug) && cat.completion[body][p.slug] > 1 ? ` ×${cat.completion[body][p.slug]}` : ""}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-bold text-muted-foreground">Painted bases ({rows.length})</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {rows.map((a, i) => (
            <div key={i} data-testid={`base-art-${a.body_model}-${a.art_phenotype}-${a.variant}`} className="rounded-lg border border-border bg-card p-1.5">
              <div className="grid aspect-square place-items-center overflow-hidden rounded bg-muted/40">
                {a.image_path ? <img src={imgUrl(a.image_path)} alt={a.art_phenotype} className="h-full w-full object-contain" /> : <span className="text-[10px] text-muted-foreground">no image</span>}
              </div>
              <p className="mt-1 truncate text-[11px] font-bold">{a.art_phenotype} v{a.variant}</p>
              <p className="truncate text-[10px] text-muted-foreground">{a.body_model}</p>
              <button data-testid={`base-art-del-${a.body_model}-${a.art_phenotype}-${a.variant}`} onClick={() => del(a)} className="mt-1 flex w-full items-center justify-center gap-1 rounded bg-rose-100 py-1 text-[10px] font-bold text-rose-600"><Trash2 size={11} /> Remove</button>
            </div>
          ))}
          {rows.length === 0 && <p className="col-span-full text-xs text-muted-foreground">No painted bases yet. HMA-bred horses show "Artwork not available yet" until their exact body + phenotype art is uploaded.</p>}
        </div>
      </div>
    </div>
  );
};

const MissingArt = () => {
  const [data, setData] = useState(null);
  const load = () => { setData(null); gameApi.get("/game/admin/hma/missing-art").then((r) => setData(r.data)).catch(() => setData({ missing: [], total: 0 })); };
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;
  return (
    <div className="space-y-2" data-testid="hma-missing-art-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Body model + art phenotype combos that have generated on HMA-bred/gathered horses but have <span className="font-bold">no painted base art yet</span>. For the art team.</p>
        <button data-testid="missing-art-refresh" onClick={load} className="flex shrink-0 items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-bold hover:bg-muted"><RotateCw size={12} /> Refresh</button>
      </div>
      {data.missing.length === 0 ? (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs font-bold text-emerald-700" data-testid="missing-art-none">✓ Every generated body + phenotype has artwork.</p>
      ) : (
        <div className="space-y-1">
          {data.missing.map((m, i) => (
            <div key={i} data-testid={`missing-art-${m.body_model}-${m.art_phenotype}`} className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs">
              <span className="font-bold text-amber-800">{m.body_model} — {m.label}</span>
              <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-800">{m.count} horse{m.count === 1 ? "" : "s"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Loading = () => <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p>;

const MARK_KEYS = [["tobiano", "Tobiano"], ["overo", "Overo"], ["lp", "Appaloosa"], ["roan", "Roan"]];
const COAT_KEYS = [["cream", "Cream"], ["dun", "Dun"], ["gray", "Gray"], ["silver", "Silver"]];
const MarkingPreview = () => {
  const [bodies, setBodies] = useState([]);
  const [body, setBody] = useState("");
  const [g, setG] = useState({ tobiano: "any", overo: "any", lp: "any", roan: "any", cream: "any", dun: "any", gray: "any", silver: "any" });
  const [extras, setExtras] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    gameApi.get("/game/admin/hma/asset-catalog").then((r) => {
      setBodies(r.data.body_models || []); setBody((b) => b || r.data.body_models?.[0] || "");
    });
  }, []);
  const roll = async () => {
    if (!body) return;
    setBusy(true);
    try {
      const p = new URLSearchParams({ body_model: body, ...g, extras: extras ? "any" : "off" });
      const r = await gameApi.get(`/game/admin/hma/preview?${p.toString()}`); setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };
  useEffect(() => { if (body) roll(); /* eslint-disable-next-line */ }, [body, g, extras]);

  const geneCtl = (k, label) => (
    <div key={k} data-testid={`preview-gene-${k}`}>
      <p className="mb-1 text-[10px] font-bold text-muted-foreground">{label}</p>
      <div className="flex overflow-hidden rounded-full border border-border">
        {["any", "on", "off"].map((st) => (
          <button key={st} data-testid={`preview-gene-${k}-${st}`} onClick={() => setG((p) => ({ ...p, [k]: st }))}
            className={`px-2.5 py-1 text-[10px] font-bold capitalize transition-colors ${g[k] === st ? (st === "on" ? "bg-emerald-500 text-white" : st === "off" ? "bg-rose-500 text-white" : "bg-primary text-primary-foreground") : "bg-background text-muted-foreground hover:bg-muted"}`}>{st}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="hma-preview-panel">
      <div className="space-y-2 rounded-xl border border-border p-3">
        <p className="flex items-center gap-1 text-sm font-bold text-primary"><Palette size={15} /> Live Horse Preview</p>
        <p className="text-[11px] text-muted-foreground">Roll a horse on any body model to see the painted base coat + markings (Appaloosa → Tobiano → Overo → legs → face) layered exactly as players will. Force each pattern or coat gene <b>On</b> / <b>Off</b> or leave it <b>Any</b> (random), then Re-roll to sample new horses with that exact combo. Turn off "Leg &amp; face markings" to view a pattern in isolation. Nothing is saved.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[10rem] flex-1"><p className="mb-1 text-[11px] font-bold">Body model</p>
            <select data-testid="preview-body" value={body} onChange={(e) => setBody(e.target.value)} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
              {bodies.map((b) => <option key={b} value={b}>{b}</option>)}
            </select></div>
          <button data-testid="preview-reroll" onClick={roll} disabled={busy} className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <RotateCw size={13} />} Re-roll
          </button>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <span className="self-center text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Markings</span>
          {MARK_KEYS.map(([k, label]) => geneCtl(k, label))}
          <label data-testid="preview-extras" className="flex cursor-pointer items-center gap-1.5 self-end pb-1 text-[11px] font-bold">
            <input type="checkbox" checked={extras} onChange={(e) => setExtras(e.target.checked)} className="h-3.5 w-3.5" /> Leg &amp; face markings
          </label>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <span className="self-center text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Coat</span>
          {COAT_KEYS.map(([k, label]) => geneCtl(k, label))}
        </div>
      </div>
      {!data ? <Loading /> : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div data-testid="preview-canvas" className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-xl border border-border bg-muted/40">
              {data.baked ? (
                <img src={imgUrl(data.baked)} alt="baked" data-testid="preview-baked-img" className="absolute inset-0 h-full w-full object-contain" style={{ zIndex: 1 }} />
              ) : (<>
                {data.base_art
                  ? <img src={imgUrl(data.base_art)} alt="base" className="absolute inset-0 h-full w-full object-contain" style={{ zIndex: 1 }} />
                  : <div className="grid h-full place-items-center px-3 text-center text-[11px] font-bold text-amber-600">Base coat art not uploaded for {data.body_model} · {data.art_phenotype_label}</div>}
                {(data.layers || []).filter((l) => !l.missing).map((l, i) => (
                  <img key={i} src={imgUrl(l.asset_id)} alt={l.slot} className="absolute inset-0 h-full w-full object-contain"
                    style={{ zIndex: i + 2, transform: `translate(${l.x || 0}%, ${l.y || 0}%) scale(${(l.scale ?? 100) / 100}) rotate(${l.rot || 0}deg)`, transformOrigin: "center" }} />
                ))}
              </>)}
            </div>
            {data.baked && <p className="mt-1 text-center text-[10px] font-bold text-emerald-600" data-testid="preview-baked-note">Server-baked (Marking Bake ON)</p>}
          </div>
          <div className="space-y-2 text-xs" data-testid="preview-info">
            <div className="rounded-lg border border-border bg-card p-2">
              <p className="font-bold text-primary">{data.base_color} <span className="text-muted-foreground">· {data.art_phenotype_label}</span></p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">Body: {data.body_model}{data.base_art_missing ? " · ❗ base art missing" : ""}</p>
            </div>
            <div className="flex flex-wrap gap-1">
              {data.traits.map((t) => <span key={t} className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">{t}</span>)}
              {data.face_markings.map((f) => <span key={f} className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700">Face: {f}</span>)}
              {data.marked_legs.map((l) => <span key={l} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Leg: {l}</span>)}
              {data.traits.length + data.face_markings.length + data.marked_legs.length === 0 && <span className="text-[11px] text-muted-foreground">Solid coat, no markings this roll.</span>}
            </div>
            {data.missing_assets?.length > 0 && (
              <div data-testid="preview-missing" className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                <p className="font-bold">❗ Marking art not uploaded for this body:</p>
                <p>{data.missing_assets.join(", ")}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const HMAGeneticsAdmin = () => {
  const [sub, setSub] = useState("genes");
  const TABS = [["genes", "Model Genetics", Dna], ["base", "Base Coat Art", Palette], ["overlays", "Marking Overlays", Palette], ["preview", "Live Preview", Eye], ["missing", "Missing Art", AlertTriangle]];
  return (
    <div className="mt-3 space-y-3" data-testid="hma-genetics-admin">
      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`hma-admin-sub-${k}`} onClick={() => setSub(k)} className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${sub === k ? "bg-primary text-primary-foreground" : "border border-border"}`}><Icon size={13} /> {l}</button>
        ))}
      </div>
      {sub === "genes" && <ModelGenes />}
      {sub === "base" && <BaseCoatArt />}
      {sub === "overlays" && <CoatAssets />}
      {sub === "preview" && <MarkingPreview />}
      {sub === "missing" && <MissingArt />}
    </div>
  );
};
